import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { t } from '@lingui/core/macro';
import theme from './QuantityStepper.module.scss';

export interface QuantityStepperProps {
    /** Current (committed) whole-number quantity. */
    value: number;
    min?: number;
    disabled?: boolean;
    /** Called with the new whole-number value once the buyer commits a change. */
    onChange: (value: number) => void;
}

/**
 * Whole-number quantity input for option groups with allowManualQuantity, rendered as a single
 * connected control (−/+ segments joined to the field). Typing commits on blur/Enter; the buttons
 * commit immediately. Values are clamped to `min` and rounded to whole numbers.
 */
export const QuantityStepper: React.FC<QuantityStepperProps> = ({ value, min = 1, disabled, onChange }) => {
    const [draft, setDraft] = useState(String(value));

    // Keep the field in sync when the committed quantity changes (e.g. after the configurator refetches).
    useEffect(() => {
        setDraft(String(value));
    }, [value]);

    const commit = (next: number) => {
        const clamped = Math.max(min, Math.round(Number.isFinite(next) ? next : value));
        setDraft(String(clamped));
        if (clamped !== value) {
            onChange(clamped);
        }
    };

    return (
        <div className={theme.stepper}>
            <button
                type="button"
                className={theme.button}
                aria-label={t`Decrease quantity`}
                disabled={disabled || value <= min}
                onClick={() => commit(value - 1)}>
                <FontAwesomeIcon icon={faMinus} />
            </button>
            <input
                type="number"
                inputMode="numeric"
                className={theme.input}
                min={min}
                step={1}
                value={draft}
                disabled={disabled}
                aria-label={t`Quantity`}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => commit(Number(draft))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.currentTarget.blur();
                    }
                }}
            />
            <button
                type="button"
                className={theme.button}
                aria-label={t`Increase quantity`}
                disabled={disabled}
                onClick={() => commit(value + 1)}>
                <FontAwesomeIcon icon={faPlus} />
            </button>
        </div>
    );
};
